<template>
  <div class="district-select-container">
    <v-select v-model="selectedDistrict" dense :hide-details="true" :items="districtList" :no-data-text="noDataText"
      :suffix="showLitigationSuffix ? '(in litigation)' : ''" data-cy="district-search">
      <template #label>
        <span style="font-size: 12px">District selection</span>
      </template>
    </v-select>
  </div>
</template>

<script>
import { EventBus } from "../../js/DataManagement/EventBus";

const MAX_SEARCH_ATTEMPTS = 5;
const RETRY_INTERVAL_MS = 500;

export default {
  name: "DistrictSearch",

  props: {
    side: {
      type: String,
      required: true,
    },
    isFinal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      districtList: [],
      selectedDistrict: "",
      pendingProposalChanges: 0,
      attemptedSearches: 0,
      tryInterval: null,
    };
  },

  computed: {
    selectedProposalTablename() {
      return this.$store.getters[`getSide${this.side}id`] || "";
    },

    selectedState() {
      return this.$store.getters.getSelectedState;
    },

    showLitigationSuffix() {
      return !this.isFinal && this.side !== 'A';
    },

    selectedLngLat() {
      return this.$store.state.selectedLngLat;
    },

    noDataText() {
      if (this.side !== "B") return "";

      const stateAbbr = this.selectedState;
      if (stateAbbr && stateAbbr.length > 0) {
        return "District plan does not exist for the selected proposal";
      }
      return "State selection needed first";
    },

    shouldAttemptRetry() {
      return (
        !this.selectedDistrict &&
        ((this.$store.getters.getSelectedDistrictSideB && this.side === "B") ||
          this.side === "A") &&
        this.attemptedSearches < MAX_SEARCH_ATTEMPTS
      );
    },
  },

  watch: {
    selectedProposalTablename(val) {
      if (val.length > 0) {
        this.onProposalChange();
      }
    },

    selectedState() {
      if (this.pendingProposalChanges === 0) {
        this.onProposalChange();
      }
    },

    selectedDistrict(val) {
      if (!val || !val.length) return;

      this.fetchAndEmitCentroid(val);
    },
  },

  mounted() {
    this.initializeComponent();
    this.setupEventListeners();
  },

  beforeDestroy() {
    this.clearRetryInterval();
    EventBus.$off("clearAddress");
    EventBus.$off(`update-side-panel-${this.side}`);
    EventBus.$off(`update-side-panel-${this.side}-proposal`);
  },

  methods: {
    initializeComponent() {
      this.$nextTick(() => {
        this.$map.once("idle", this.onProposalChange);
      });
    },

    setupEventListeners() {
      EventBus.$on("clearAddress", () => {
        this.selectedDistrict = "";
      });

      EventBus.$on(`update-side-panel-${this.side}`, this.handleSidePanelUpdate);

      EventBus.$on(`update-side-panel-${this.side}-proposal`, () => {
        if (this.selectedDistrict && this.selectedDistrict.value) {
          const selected = this.$querystringManager.getRoute().query.selected;
          const selectedLngLat = selected?.split(",").map(l => +l) ?? [];

          if (selectedLngLat.length === 2) {
            EventBus.$emit("select-from-district-search", selectedLngLat, false);
          } else {
            this.fetchAndEmitCentroid(this.selectedDistrict.value);
          }
        }
      });
    },

    handleSidePanelUpdate(data) {
      if (data.geoid && data.geoid.substring) {
        const stateFromGeoid = data.geoid.substring(0, 2);
        if (this.selectedState !== stateFromGeoid) {
          this.$store.commit("setSelectedState", stateFromGeoid);
        }
      }

      this.$map.once("idle", () => {
        this.selectDistrictFromData(data);
      });
    },

    selectDistrictFromData(data) {
      this.selectedDistrict = this.districtList.find(
        district => district.value === data.geoid
      );

      if (this.selectedDistrict) {
        this.emitYouSelectedEvent(data.side);
      } else if (this.shouldAttemptRetry) {
        this.startRetryInterval(data);
      }
    },

    emitYouSelectedEvent(side) {
      // finds the state name corresponding to the currently selected state, then broadcasts event to show the district highlighted
      const state = this.$stateData.states.find(
        s => s.statefips === this.selectedState
      );

      if (state) {
        EventBus.$emit('update-you-selected', {
          side,
          text: this.selectedDistrict.text,
          state: state.statename,
        });
      }
    },

    startRetryInterval(data) {
      this.clearRetryInterval();
      this.attemptedSearches = 0;
      this.tryInterval = setInterval(
        () => this.retryDistrictSelection(data),
        RETRY_INTERVAL_MS
      );
    },

    retryDistrictSelection(data) {
      if (this.shouldAttemptRetry) {
        this.attemptedSearches++;
        this.selectedDistrict = this.districtList.find(
          district => district.value === data.geoid
        );
      } else {
        this.clearRetryInterval();
      }
    },

    clearRetryInterval() {
      if (this.tryInterval) {
        clearInterval(this.tryInterval);
        this.tryInterval = null;
        this.attemptedSearches = 0;
      }
    },

    async onProposalChange() {
      if (this.selectedProposalTablename.length < 1) return;

      this.pendingProposalChanges++;

      try {
        const resp = await this.$dataManager.getDistrictNameList(
          this.selectedProposalTablename,
          this.selectedState
        );

        this.pendingProposalChanges--;

        if (this.pendingProposalChanges === 0) {
          this.districtList = resp.data
            .sort((a, b) => +a.geoid - +b.geoid)
            .map(district => ({
              text: district.namelsad,
              value: district.geoid,
            }));

          this.$store.commit("setSelectedState", this.selectedState);
        }
      } catch (error) {
        this.pendingProposalChanges--;
        console.error("Error fetching district list:", error);
      }
    },

    async fetchAndEmitCentroid(geoid) {
      try {
        const resp = await this.$dataManager.getCentroidForGeoid(
          this.selectedProposalTablename,
          geoid
        );

        const coordinates = [resp.data[0].x, resp.data[0].y];
        EventBus.$emit("select-from-district-search", coordinates, true);
        this.$store.commit("setSelectedLngLat", coordinates);
      } catch (error) {
        console.error("Error fetching centroid:", error);
      }
    },
  },
};
</script>

<style scoped>
.form-group {
  white-space: nowrap;
}
</style>

<style>
.district-select-container,
.district-select-container .v-label {
  margin-top: 4px !important;
}
</style>